import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountType,
    IAccount,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IFinancialStatementInterpretation,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType, FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { defaultSystemRules } from '../constants/defaultSystemRules.constant';
import { ICalculation } from '../interfaces/ICalculation.interface';
import { getFlipRule } from '../mappers/account-mapping-template.mapper';
import { isFli, isGroup, isSubTotal } from '../type-guards/type-guards';

export class AccountMappingEngineHelper {
    static getCorrectSign(
        fli: IAccountMappingTemplateGroupItemFLI,
        account: IAccount,
        sheetSystemRules: IFinancialStatementInterpretation,
    ): IAccount {
        const positiveSign = 1;
        const negativeSign = -1;
        let flipSign = false;

        if (fli != null) {
            flipSign = this.getFlipSigns(fli.fliType, sheetSystemRules);
        }
        const sign = flipSign ? negativeSign : positiveSign;
        return {
            ...account,
            calculatedOpeningBalance: NumberUtils.multiplyNumbers([account.openingBalance, sign]),
            calculatedClosingBalance: NumberUtils.multiplyNumbers([account.closingBalance, sign]),
        };
    }

    static getFlipSigns(fliType: FinancialLineItemType, sheetSystemRules: IFinancialStatementInterpretation): boolean {
        return getFlipRule(defaultSystemRules, sheetSystemRules, fliType);
    }

    static getNestedGroupByName(
        topGroup: IAccountMappingTemplateGroup,
        groupName: string,
    ): IAccountMappingTemplateGroup | undefined {
        if (topGroup.name === groupName) {
            return topGroup;
        }

        if (topGroup.groups.length !== EMPTY_ARRAY_LENGTH) {
            for (const group of topGroup.groups) {
                const foundGroup = AccountMappingEngineHelper.getNestedGroupByName(group, groupName);
                if (foundGroup != null) {
                    return foundGroup;
                }
            }
        }

        return undefined;
    }

    static createUncoveredShortfallAccount(
        equityGroupCalculatedClosingBalance: number,
        equityGroupCalculatedOpeningBalance: number,
        uncoveredShortfallAssetsAccountName: string,
        accountNumber: string,
    ): IAccount {
        const isClosingValueNegative = equityGroupCalculatedClosingBalance < 0;
        const isOpeningValueNegative = equityGroupCalculatedOpeningBalance < 0;

        return {
            accountNumber: accountNumber,
            name: uncoveredShortfallAssetsAccountName,
            type: AccountType.UNCOVERED_SHORTFALL,
            openingBalance: isOpeningValueNegative
                ? NumberUtils.multiplyNumbers([equityGroupCalculatedOpeningBalance, -1])
                : 0,
            closingBalance: isClosingValueNegative
                ? NumberUtils.multiplyNumbers([equityGroupCalculatedClosingBalance, -1])
                : 0,
            calculatedOpeningBalance: isOpeningValueNegative
                ? NumberUtils.multiplyNumbers([equityGroupCalculatedOpeningBalance, -1])
                : 0,
            calculatedClosingBalance: isClosingValueNegative
                ? NumberUtils.multiplyNumbers([equityGroupCalculatedClosingBalance, -1])
                : 0,
        };
    }

    static getFliFromNestedGroupsByName(
        group: IAccountMappingTemplateGroup,
        fliName: string,
    ): IAccountMappingTemplateGroupItemFLI | undefined {
        for (const item of group.items) {
            if (item.type === ItemType.FLI && item.name === fliName) {
                return item;
            }
        }

        for (const subgroup of group.groups) {
            const found = AccountMappingEngineHelper.getFliFromNestedGroupsByName(subgroup, fliName);
            if (found != null) {
                return found;
            }
        }

        return undefined;
    }

    static createUncoveredShortfallEquityFli(
        uncoveredShortfallEquityFliCaption: string,
    ): IAccountMappingTemplateGroupItemFLI {
        return {
            name: 'uncovered-shortfall-equity-fli',
            caption: uncoveredShortfallEquityFliCaption,
            type: ItemType.FLI,
            accounts: [],
            fliType: FinancialLineItemType.LIABILITIES,
            cashFlowType: FinancialLineItemCashFlowType.NONE,
            reclassesAccounts: [],
            calculatedClosingBalance: 0,
            calculatedOpeningBalance: 0,
        } as IAccountMappingTemplateGroupItemFLI;
    }

    static createUncoveredShortfallAssetsFli(
        uncoveredShortfallAssetsFliCaption: string,
    ): IAccountMappingTemplateGroupItemFLI {
        return {
            name: 'uncovered-shortfall-assets-fli',
            caption: uncoveredShortfallAssetsFliCaption,
            type: ItemType.FLI,
            accounts: [],
            fliType: FinancialLineItemType.ASSETS,
            cashFlowType: FinancialLineItemCashFlowType.NONE,
            reclassesAccounts: [],
            calculatedClosingBalance: 0,
            calculatedOpeningBalance: 0,
        } as IAccountMappingTemplateGroupItemFLI;
    }

    static calculateGroupWithoutFlisForFCP(
        group: IAccountMappingTemplateGroup,
        parentSums: Map<string, ICalculation>,
        rebase: boolean = false,
    ): ICalculation {
        let sum: ICalculation = this.getEmptySum();
        parentSums.set(group.name, sum);

        for (const sortedItem of group.sortedItems) {
            if (isGroup(sortedItem as SortedMappingItem)) {
                const iterationGroupSum = this.calculateGroupWithoutFlisForFCP(
                    sortedItem as IAccountMappingTemplateGroup,
                    parentSums,
                    rebase,
                );

                sum = this.addValueToSum(sum, iterationGroupSum);
            } else if (isSubTotal(sortedItem as SortedMappingItem)) {
                // Use the name property for the lookup

                const groupName = (sortedItem as IAccountMappingTemplateGroupItemSubtotal).scope.name;

                // Get the value from the map based on the groupName
                const parentSum = parentSums.get(groupName);
                AccountMappingEngineHelper.assignCalculationItems(
                    sortedItem as IAccountMappingTemplateGroupItemSubtotal,
                    parentSum,
                );
            } else if (isFli(sortedItem as SortedMappingItem)) {
                const fliSum: ICalculation = {
                    calculatedOpeningBalance: (sortedItem as IAccountMappingTemplateGroupItemFLI)
                        .calculatedOpeningBalance,
                    calculatedClosingBalance: (sortedItem as IAccountMappingTemplateGroupItemFLI)
                        .calculatedClosingBalance,
                };

                sum = this.addValueToSum(sum, fliSum);
                parentSums.forEach((value, key) => {
                    const valueSum = this.addValueToSum(value, fliSum);
                    parentSums.set(key, valueSum);
                });
            }
        }

        AccountMappingEngineHelper.assignCalculationItems(group, sum);
        parentSums.delete(group.name);
        return sum;
    }

    static getEmptySum(): ICalculation {
        return {
            calculatedClosingBalance: 0,
            calculatedOpeningBalance: 0,
            openingReClass: 0,
            openingAdjustment: 0,
            closingReClass: 0,
            closingAdjustment: 0,
        };
    }

    static addValueToSum(sum: ICalculation, val: Partial<ICalculation>): ICalculation {
        const calculatedClosingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.calculatedClosingBalance, val.calculatedClosingBalance],
            0,
        );

        const calculatedOpeningBalance = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.calculatedOpeningBalance, val.calculatedOpeningBalance],
            0,
        );

        const closingAdjustment = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.closingAdjustment, val.closingAdjustment],
            undefined,
        );

        const closingReClass = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.closingReClass, val.closingReClass],
            undefined,
        );

        const openingAdjustment = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.openingAdjustment, val.openingAdjustment],
            undefined,
        );

        const openingReClass = NumberUtils.sumIgnoringInfiniteNumbers(
            [sum.openingReClass, val.openingReClass],
            undefined,
        );

        return {
            calculatedClosingBalance,
            calculatedOpeningBalance,
            closingAdjustment,
            openingAdjustment,
            closingReClass,
            openingReClass,
        };
    }
    static assignCalculationItems(target: Partial<ICalculation>, sum: ICalculation): void {
        let closingAdjustment: number | undefined;
        if (sum.closingAdjustment != null && NumberUtils.isFinite(sum.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(sum.closingAdjustment);
        }

        let closingReClass: number | undefined;
        if (sum.closingReClass != null && NumberUtils.isFinite(sum.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(sum.closingReClass);
        }

        let openingAdjustment: number | undefined;
        if (sum.openingAdjustment != null && NumberUtils.isFinite(sum.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(sum.openingAdjustment);
        }

        let openingReClass: number | undefined;
        if (sum.openingReClass != null && NumberUtils.isFinite(sum.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(sum.openingReClass);
        }
        target.calculatedOpeningBalance = NumberUtils.roundNumber(sum.calculatedOpeningBalance);
        target.calculatedClosingBalance = NumberUtils.roundNumber(sum.calculatedClosingBalance);
        target.closingAdjustment = closingAdjustment;
        target.closingReClass = closingReClass;
        target.openingAdjustment = openingAdjustment;
        target.openingReClass = openingReClass;
    }
}
